import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiBarChart2,
  FiCheck,
  FiCheckCircle,
  FiCheckSquare,
  FiClock,
  FiEdit2,
  FiFlag,
  FiPause,
  FiPlay,
  FiPlus,
  FiTarget,
  FiTrash2,
  FiX,
  FiXCircle,
} from "react-icons/fi";
import { metaService, taskService } from "../api";
import style from "../style/Metas.module.css";

const URGENCIAS = [
  { value: "importante", label: "Importante" },
  { value: "urgente", label: "Urgente" },
  { value: "obligaciones", label: "Obligación" },
  { value: "no importante", label: "Sin prisa" },
];
// Colores de tarea (mismos que en Tareas) para elegir el fondo.
const COLORES_TAREA = [
  { key: "color1", grad: "linear-gradient(135deg, #9cfb43, #5dc72d)" },
  { key: "color2", grad: "linear-gradient(135deg, #ff9e45, #ff6b2f)" },
  { key: "color3", grad: "linear-gradient(135deg, #ffe15a, #e3b72f)" },
  { key: "color4", grad: "linear-gradient(135deg, #7be7ad, #35c981)" },
  { key: "color5", grad: "linear-gradient(135deg, #88d8ff, #3f9fe7)" },
  { key: "color6", grad: "linear-gradient(135deg, #ffabc9, #ea5e9a)" },
  { key: "color7", grad: "linear-gradient(135deg, #c9b6ff, #8b6ee8)" },
  { key: "color8", grad: "linear-gradient(135deg, #ff8d8d, #e05252)" },
];

const hoyYMD = () => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const HORIZONTES = [
  { value: "corto", label: "Corto plazo", hint: "próximas semanas / meses" },
  { value: "mediano", label: "Mediano plazo", hint: "dentro de este año" },
  { value: "largo", label: "Largo plazo", hint: "a uno o más años" },
];

const AREAS_EJEMPLO = ["Finanzas", "Salud", "Carrera", "Personal", "Aprendizaje"];

// Sugerencias de área = ejemplos + las áreas que ya usaste en tus metas, sin
// repetir (sin distinguir mayúsculas). Las usadas van primero.
// Áreas creadas a mano con "+ Agregar nueva" (quedan guardadas en el navegador
// aunque todavía ninguna meta las use).
const AREAS_KEY = "gw-metas-areas";
const leerAreasGuardadas = () => {
  try {
    const raw = localStorage.getItem(AREAS_KEY);
    const lista = raw ? JSON.parse(raw) : [];
    return Array.isArray(lista) ? lista.filter((a) => typeof a === "string" && a.trim()) : [];
  } catch {
    return [];
  }
};
const guardarAreas = (lista) => {
  try {
    localStorage.setItem(AREAS_KEY, JSON.stringify(lista));
  } catch {
    /* nada */
  }
};

const areasSugeridas = (metas, extras = []) => {
  const usadas = [];
  const vistas = new Set();
  const agregar = (a) => {
    const texto = String(a || "").trim();
    const k = texto.toLowerCase();
    if (texto && !vistas.has(k)) {
      vistas.add(k);
      usadas.push(texto);
    }
  };
  metas.forEach((m) => agregar(m.area));
  extras.forEach(agregar);
  const extra = AREAS_EJEMPLO.filter((a) => !vistas.has(a.toLowerCase()));
  return [...usadas, ...extra];
};

const NUEVA_AREA = "__nueva_area__";

const MEDICIONES = [
  { value: "hitos", label: "Por hitos", hint: "una checklist de pasos" },
  { value: "numero", label: "Por número", hint: "ej: ahorrar $500.000" },
  { value: "manual", label: "Manual", hint: "ajustás el % vos" },
];

const FORM_VACIO = {
  id: null,
  titulo: "",
  descripcion: "",
  horizonte: "corto",
  area: "",
  fechaObjetivo: "",
  medicion: "hitos",
  hitos: [],
  objetivoNumero: "",
  actualNumero: "",
  unidad: "",
  progresoManual: 0,
};

const hoyLocal = () => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

// Progreso 0-100 según cómo se mide la meta.
const progressOf = (m) => {
  if (m.estado === "completada") return 100;
  if (m.medicion === "hitos") {
    const total = m.hitos?.length || 0;
    if (!total) return 0;
    return Math.round((100 * m.hitos.filter((h) => h.hecho).length) / total);
  }
  if (m.medicion === "numero") {
    const objetivo = Number(m.objetivoNumero) || 0;
    if (objetivo <= 0) return 0;
    return Math.min(100, Math.round((100 * (Number(m.actualNumero) || 0)) / objetivo));
  }
  return Math.min(100, Math.max(0, Math.round(Number(m.progresoManual) || 0)));
};

// Días que faltan para la fecha objetivo (negativo = vencida).
const daysUntil = (fecha) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fecha || "")) return null;
  const [y, m, d] = fecha.split("-").map(Number);
  const [hy, hm, hd] = hoyLocal().split("-").map(Number);
  return Math.round(
    (Date.UTC(y, m - 1, d) - Date.UTC(hy, hm - 1, hd)) / 86400000
  );
};

// Suma días a una fecha YYYY-MM-DD (en local, sin líos de zona horaria).
const sumarDias = (ymd, n) => {
  const [y, m, d] = ymd.split("-").map(Number);
  const fecha = new Date(y, m - 1, d + n);
  const mm = String(fecha.getMonth() + 1).padStart(2, "0");
  const dd = String(fecha.getDate()).padStart(2, "0");
  return `${fecha.getFullYear()}-${mm}-${dd}`;
};

const fmtFechaCorta = (ymd) => (ymd ? ymd.split("-").reverse().join("/") : "");

const dueLabel = (m) => {
  if (m.estado === "completada") {
    return m.completadaEn ? `Completada el ${fmtFechaCorta(m.completadaEn)}` : "Completada";
  }
  if (m.estado === "no_cumplida") {
    return m.cerradaEn ? `Cerrada sin cumplir el ${fmtFechaCorta(m.cerradaEn)}` : "Cerrada sin cumplir";
  }
  const dias = daysUntil(m.fechaObjetivo);
  if (dias === null) return "Sin fecha límite";
  if (dias === 0) return "Vence hoy";
  if (dias > 0) return `${dias === 1 ? "Falta 1 día" : `Faltan ${dias} días`}`;
  return `Venció hace ${Math.abs(dias)} ${Math.abs(dias) === 1 ? "día" : "días"}`;
};

const fmtNum = (n) => Number(n || 0).toLocaleString("es-AR");

// "3 de 12 libros" para unidades-palabra; "$3.000 de $12.000" para símbolos.
const esSimbolo = (u) => /^[^\p{L}\p{N}]{1,3}$/u.test(String(u || "").trim());
const conUnidad = (n, unidad) => {
  const u = String(unidad || "").trim();
  if (!u) return fmtNum(n);
  return esSimbolo(u) ? `${u}${fmtNum(n)}` : `${fmtNum(n)} ${u}`;
};

// Colores por plazo (los mismos de las pills).
const PLAZO_COLORS = { corto: "#5b8ad6", mediano: "#c9a23a", largo: "#b06ad6" };

// Gradiente cónico para el donut (mismo recurso que la página de Métricas).
const buildConicGradient = (items) => {
  const total = items.reduce((acc, item) => acc + item.value, 0);
  if (!total) return "conic-gradient(rgba(127,137,129,0.25) 0 100%)";
  let acumulado = 0;
  const stops = items
    .filter((item) => item.value > 0)
    .map((item) => {
      const desde = (acumulado / total) * 100;
      acumulado += item.value;
      const hasta = (acumulado / total) * 100;
      return `${item.color} ${desde}% ${hasta}%`;
    });
  return `conic-gradient(${stops.join(", ")})`;
};

function MetasPage({ activeWorkspace }) {
  const [metas, setMetas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [horizonteFiltro, setHorizonteFiltro] = useState("todas");
  const [estadoFiltro, setEstadoFiltro] = useState("activa");
  const [detalle, setDetalle] = useState(null); // meta abierta
  const [extMenu, setExtMenu] = useState(null); // id de la meta con el menú "Extender plazo" abierto
  const [form, setForm] = useState(null); // formulario crear/editar
  const [taskLink, setTaskLink] = useState(null); // hito -> tarea { meta, indice, texto }
  const [taskForm, setTaskForm] = useState(null); // datos del form de tarea
  const [taskSaving, setTaskSaving] = useState(false);
  const [chartsOpen, setChartsOpen] = useState(
    () => typeof localStorage !== "undefined" && localStorage.getItem("gw-metas-charts") === "1"
  );

  const toggleCharts = () => {
    setChartsOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("gw-metas-charts", next ? "1" : "0");
      } catch {
        /* nada */
      }
      return next;
    });
  };
  const [hitoNuevo, setHitoNuevo] = useState("");
  const [areasExtra, setAreasExtra] = useState(leerAreasGuardadas); // áreas agregadas a mano
  const [areaNueva, setAreaNueva] = useState(null); // texto del campo "+ Agregar nueva" (null = cerrado)

  const confirmarAreaNueva = () => {
    const texto = String(areaNueva || "").trim().slice(0, 60);
    if (!texto) {
      setAreaNueva(null);
      return;
    }
    setAreasExtra((prev) => {
      const yaEsta = prev.some((a) => a.toLowerCase() === texto.toLowerCase());
      const lista = yaEsta ? prev : [...prev, texto];
      guardarAreas(lista);
      return lista;
    });
    setForm((prev) => (prev ? { ...prev, area: texto } : prev));
    setAreaNueva(null);
  };
  const [numeroNuevo, setNumeroNuevo] = useState("");
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const { data } = await metaService.getAll();
      setMetas(Array.isArray(data) ? data : []);
    } catch {
      /* dejamos lo que haya */
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    setCargando(true);
    cargar();
  }, [cargar, activeWorkspace]);

  // Mantiene la lista y el panel de detalle sincronizados tras un update.
  const reemplazar = (metaActualizada) => {
    setMetas((prev) => prev.map((m) => (m._id === metaActualizada._id ? metaActualizada : m)));
    setDetalle((prev) => (prev && prev._id === metaActualizada._id ? metaActualizada : prev));
  };

  const actualizar = async (meta, cambios) => {
    try {
      const { data } = await metaService.update(meta._id, cambios);
      reemplazar(data);
      return data;
    } catch {
      return null;
    }
  };

  // Al elegir "Agregar a tarea" en un hito, precargamos el form de tarea.
  useEffect(() => {
    if (taskLink) {
      setTaskForm({
        meta: taskLink.texto,
        fecha: hoyYMD(),
        horario: "",
        urgencia: "importante",
        color: "color1",
      });
    } else {
      setTaskForm(null);
    }
  }, [taskLink]);

  const guardarTareaDeHito = async (event) => {
    event.preventDefault();
    if (!taskForm?.meta.trim() || taskSaving) return;
    setTaskSaving(true);
    try {
      await taskService.create({
        meta: taskForm.meta.trim(),
        tipo: "task",
        fecha: taskForm.fecha,
        horario: taskForm.horario || "",
        urgencia: taskForm.urgencia,
        color: taskForm.color,
        metaId: taskLink.meta._id,
        hitoIndex: taskLink.indice,
      });
      setTaskLink(null);
      // Recargamos para ver el hito ya vinculado ("En tareas").
      const { data } = await metaService.getAll();
      const lista = Array.isArray(data) ? data : [];
      setMetas(lista);
      setDetalle((prev) => (prev ? lista.find((m) => m._id === prev._id) || prev : prev));
    } catch {
      /* nada */
    } finally {
      setTaskSaving(false);
    }
  };

  const toggleHito = (meta, indice) => {
    const hitos = meta.hitos.map((h, i) => (i === indice ? { ...h, hecho: !h.hecho } : h));
    reemplazar({ ...meta, hitos }); // optimista
    actualizar(meta, { hitos });
  };

  const agregarHito = (meta) => {
    const texto = hitoNuevo.trim();
    if (!texto) return;
    const hitos = [...(meta.hitos || []), { texto, hecho: false }];
    setHitoNuevo("");
    reemplazar({ ...meta, hitos });
    actualizar(meta, { hitos });
  };

  const borrarHito = (meta, indice) => {
    const hitos = meta.hitos.filter((_, i) => i !== indice);
    reemplazar({ ...meta, hitos });
    actualizar(meta, { hitos });
  };

  const registrarAvance = (meta) => {
    const valor = Number(numeroNuevo);
    if (!Number.isFinite(valor)) return;
    setNumeroNuevo("");
    reemplazar({ ...meta, actualNumero: valor });
    actualizar(meta, { actualNumero: valor });
  };

  const cambiarManual = (meta, valor) => {
    reemplazar({ ...meta, progresoManual: valor });
  };

  const guardarManual = (meta, valor) => {
    const v = Math.min(100, Math.max(0, Math.round(Number(valor) || 0)));
    reemplazar({ ...meta, progresoManual: v, progresoManualAt: hoyLocal() });
    actualizar(meta, { progresoManual: v, fechaLocal: hoyLocal() });
  };

  // Suma (o resta) un delta al avance manual y lo guarda.
  const sumarManual = (meta, delta) => {
    guardarManual(meta, (Number(meta.progresoManual) || 0) + delta);
  };

  const cambiarEstado = (meta, estado) => {
    reemplazar({
      ...meta,
      estado,
      completadaEn: estado === "completada" ? hoyLocal() : "",
      cerradaEn: estado === "no_cumplida" ? hoyLocal() : "",
    });
    actualizar(meta, { estado, fechaLocal: hoyLocal() });
  };

  // Meta vencida: corre la fecha objetivo (contando desde hoy) y la deja activa.
  const extenderPlazo = (meta, fechaObjetivo) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(fechaObjetivo || "")) return;
    setExtMenu(null);
    reemplazar({ ...meta, fechaObjetivo });
    actualizar(meta, { fechaObjetivo });
  };

  // El menú de extender se cierra al clickear afuera.
  useEffect(() => {
    if (!extMenu) return undefined;
    const cerrar = (e) => {
      if (!e.target.closest?.("[data-ext-menu]")) setExtMenu(null);
    };
    document.addEventListener("mousedown", cerrar);
    return () => document.removeEventListener("mousedown", cerrar);
  }, [extMenu]);

  const eliminar = async (meta) => {
    const seguro = window.confirm(`¿Eliminar la meta “${meta.titulo}”?`);
    if (!seguro) return;
    try {
      await metaService.delete(meta._id);
      setMetas((prev) => prev.filter((m) => m._id !== meta._id));
      setDetalle(null);
    } catch {
      /* nada */
    }
  };

  const abrirCrear = () => {
    setForm({ ...FORM_VACIO });
  };

  const abrirEditar = (meta) => {
    setForm({
      id: meta._id,
      titulo: meta.titulo || "",
      descripcion: meta.descripcion || "",
      horizonte: meta.horizonte || "corto",
      area: meta.area || "",
      fechaObjetivo: meta.fechaObjetivo || "",
      medicion: meta.medicion || "hitos",
      hitos: (meta.hitos || []).map((h) => ({ ...h })),
      objetivoNumero: meta.objetivoNumero || "",
      actualNumero: meta.actualNumero || "",
      unidad: meta.unidad || "",
      progresoManual: meta.progresoManual || 0,
    });
  };

  const guardarForm = async (event) => {
    event.preventDefault();
    if (!form.titulo.trim() || guardando) return;
    setGuardando(true);
    const payload = {
      titulo: form.titulo,
      descripcion: form.descripcion,
      horizonte: form.horizonte,
      area: form.area,
      fechaObjetivo: form.fechaObjetivo,
      medicion: form.medicion,
      hitos: form.hitos,
      objetivoNumero: Number(form.objetivoNumero) || 0,
      actualNumero: Number(form.actualNumero) || 0,
      unidad: form.unidad,
      progresoManual: Number(form.progresoManual) || 0,
    };
    try {
      if (form.id) {
        const { data } = await metaService.update(form.id, payload);
        reemplazar(data);
      } else {
        const { data } = await metaService.create(payload);
        setMetas((prev) => [data, ...prev]);
      }
      setForm(null);
    } catch {
      /* se reintenta guardando de nuevo */
    } finally {
      setGuardando(false);
    }
  };

  const visibles = useMemo(() => {
    return metas.filter((m) => {
      if (horizonteFiltro !== "todas" && m.horizonte !== horizonteFiltro) return false;
      if (estadoFiltro !== "todas" && m.estado !== estadoFiltro) return false;
      return true;
    });
  }, [metas, horizonteFiltro, estadoFiltro]);

  const stats = useMemo(() => {
    const activas = metas.filter((m) => m.estado === "activa");
    const completadas = metas.filter((m) => m.estado === "completada");
    const promedio = activas.length
      ? Math.round(activas.reduce((acc, m) => acc + progressOf(m), 0) / activas.length)
      : 0;
    return { activas: activas.length, completadas: completadas.length, promedio };
  }, [metas]);

  // Datos para los gráficos: distribución por plazo y avance de las activas.
  const plazoItems = useMemo(
    () =>
      HORIZONTES.map((h) => ({
        label: h.label,
        color: PLAZO_COLORS[h.value],
        value: metas.filter(
          (m) => m.horizonte === h.value && !["completada", "no_cumplida"].includes(m.estado)
        ).length,
      })),
    [metas]
  );

  const barrasAvance = useMemo(
    () =>
      metas
        .filter((m) => m.estado === "activa")
        .map((m) => ({ id: m._id, titulo: m.titulo, progreso: progressOf(m), horizonte: m.horizonte }))
        .sort((a, b) => b.progreso - a.progreso)
        .slice(0, 6),
    [metas]
  );

  const medidaLabel = (m) => {
    if (m.medicion === "hitos") {
      const hechos = (m.hitos || []).filter((h) => h.hecho).length;
      return `${hechos} de ${(m.hitos || []).length} hitos`;
    }
    if (m.medicion === "numero") {
      return esSimbolo(m.unidad)
        ? `${conUnidad(m.actualNumero, m.unidad)} de ${conUnidad(m.objetivoNumero, m.unidad)}`
        : `${fmtNum(m.actualNumero)} de ${conUnidad(m.objetivoNumero, m.unidad)}`;
    }
    return "Avance manual";
  };

  const horizonteLabel = (value) => HORIZONTES.find((h) => h.value === value)?.label || value;

  /* ===== Acciones de una meta vencida: extender el plazo o cerrarla ===== */
  const renderVencidaAcciones = (meta) => {
    const hoy = hoyLocal();
    const abierto = extMenu === meta._id;
    return (
      <div className={style.vencidaAcciones} onClick={(e) => e.stopPropagation()}>
        <div className={style.extWrap} data-ext-menu="true">
          <button
            type="button"
            className={`${style.btnVencida} ${abierto ? style.btnVencidaActivo : ""}`}
            onClick={() => setExtMenu(abierto ? null : meta._id)}
            aria-expanded={abierto}
          >
            <FiClock /> Extender plazo
          </button>
          {abierto ? (
            <div className={style.extMenu} role="menu">
              <button type="button" className={style.extOpcion} onClick={() => extenderPlazo(meta, sumarDias(hoy, 7))}>
                +7 días <small>{fmtFechaCorta(sumarDias(hoy, 7))}</small>
              </button>
              <button type="button" className={style.extOpcion} onClick={() => extenderPlazo(meta, sumarDias(hoy, 30))}>
                +30 días <small>{fmtFechaCorta(sumarDias(hoy, 30))}</small>
              </button>
              <label className={style.extFecha}>
                Elegir fecha
                <input
                  type="date"
                  min={hoy}
                  onChange={(e) => extenderPlazo(meta, e.target.value)}
                />
              </label>
            </div>
          ) : null}
        </div>
        <button
          type="button"
          className={`${style.btnVencida} ${style.btnCerrar}`}
          onClick={() => cambiarEstado(meta, "no_cumplida")}
          title="Cerrar la meta sin cumplirla (podés reactivarla después)"
        >
          <FiXCircle /> Cerrar
        </button>
      </div>
    );
  };

  /* ===== Render de una card ===== */
  const renderCard = (meta) => {
    const progreso = progressOf(meta);
    const dias = daysUntil(meta.fechaObjetivo);
    const vencida = meta.estado === "activa" && dias !== null && dias < 0;

    return (
      <div
        role="button"
        tabIndex={0}
        key={meta._id}
        className={`${style.card} ${style[`card_${meta.horizonte}`] || ""} ${
          meta.estado === "completada" ? style.cardCompletada : ""
        } ${meta.estado === "pausada" ? style.cardPausada : ""} ${
          meta.estado === "no_cumplida" ? style.cardNoCumplida : ""
        }`}
        onClick={() => setDetalle(meta)}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setDetalle(meta);
          }
        }}
      >
        <div className={style.cardTop}>
          <span className={`${style.pill} ${style[`pill_${meta.horizonte}`]}`}>
            {horizonteLabel(meta.horizonte)}
          </span>
          {meta.area ? <span className={style.pillArea}>{meta.area}</span> : null}
          {meta.estado === "pausada" ? (
            <span className={style.pillPausada}>En pausa</span>
          ) : null}
          {meta.estado === "no_cumplida" ? (
            <span className={style.pillNoCumplida}>No cumplida</span>
          ) : null}
        </div>

        <h3 className={style.cardTitulo}>{meta.titulo}</h3>
        {meta.descripcion ? <p className={style.cardDesc}>{meta.descripcion}</p> : null}

        <div className={style.progressRow}>
          <div className={style.progressTrack}>
            <div
              className={`${style.progressFill} ${progreso >= 100 ? style.progressDone : ""}`}
              style={{ width: `${progreso}%` }}
            />
          </div>
          <span className={style.progressPct}>{progreso}%</span>
        </div>

        <div className={style.cardFoot}>
          <span className={style.medida}>{medidaLabel(meta)}</span>
          <span className={`${style.due} ${vencida ? style.dueVencida : ""}`}>
            {dueLabel(meta)}
          </span>
        </div>
        {vencida ? renderVencidaAcciones(meta) : null}
      </div>
    );
  };

  /* ===== Panel de detalle ===== */
  const renderDetalle = () => {
    const meta = detalle;
    const progreso = progressOf(meta);
    const todosHechos =
      meta.medicion === "hitos" && meta.hitos.length > 0 && meta.hitos.every((h) => h.hecho);

    return (
      <div className={style.overlay} onClick={() => setDetalle(null)}>
        <div className={style.panel} onClick={(e) => e.stopPropagation()}>
          <div className={style.panelHead}>
            <div className={style.panelPills}>
              <span className={`${style.pill} ${style[`pill_${meta.horizonte}`]}`}>
                {horizonteLabel(meta.horizonte)}
              </span>
              {meta.area ? <span className={style.pillArea}>{meta.area}</span> : null}
            </div>
            <div className={style.panelHeadActions}>
              <button
                type="button"
                className={style.iconBtn}
                onClick={() => abrirEditar(meta)}
                title="Editar meta"
                aria-label="Editar meta"
              >
                <FiEdit2 />
              </button>
              <button
                type="button"
                className={`${style.iconBtn} ${style.iconBtnRojo}`}
                onClick={() => eliminar(meta)}
                title="Eliminar meta"
                aria-label="Eliminar meta"
              >
                <FiTrash2 />
              </button>
              <button
                type="button"
                className={style.iconBtn}
                onClick={() => setDetalle(null)}
                aria-label="Cerrar"
              >
                <FiX />
              </button>
            </div>
          </div>

          <h2 className={style.panelTitulo}>{meta.titulo}</h2>
          {meta.descripcion ? <p className={style.panelDesc}>{meta.descripcion}</p> : null}
          <p className={style.panelDue}>{dueLabel(meta)}</p>

          <div className={style.progressRow}>
            <div className={style.progressTrack}>
              <div
                className={`${style.progressFill} ${progreso >= 100 ? style.progressDone : ""}`}
                style={{ width: `${progreso}%` }}
              />
            </div>
            <span className={style.progressPct}>{progreso}%</span>
          </div>

          {/* Avance según el tipo de medición */}
          {meta.medicion === "hitos" ? (
            <div className={style.hitosBox}>
              <p className={style.boxLabel}>Hitos</p>
              {meta.hitos.length === 0 ? (
                <p className={style.hitosVacio}>
                  Sumá pasos chicos: hacen que la meta grande se sienta alcanzable.
                </p>
              ) : (
                <ul className={style.hitosLista}>
                  {meta.hitos.map((hito, indice) => (
                    <li key={indice} className={style.hitoRow}>
                      <div className={style.hitoItem}>
                        <button
                          type="button"
                          className={`${style.hitoCheck} ${hito.hecho ? style.hitoCheckOn : ""}`}
                          onClick={() => toggleHito(meta, indice)}
                          aria-label={hito.hecho ? "Desmarcar hito" : "Marcar hito"}
                        >
                          {hito.hecho ? <FiCheck /> : null}
                        </button>
                        <span className={`${style.hitoTexto} ${hito.hecho ? style.hitoHecho : ""}`}>
                          {hito.texto}
                        </span>
                        <button
                          type="button"
                          className={style.hitoBorrar}
                          onClick={() => borrarHito(meta, indice)}
                          aria-label="Borrar hito"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                      {hito.taskId ? (
                        <span className={style.hitoEnTareas}>
                          <FiCheckSquare /> En tareas
                        </span>
                      ) : (
                        <button
                          type="button"
                          className={style.hitoAddTarea}
                          onClick={() => setTaskLink({ meta, indice, texto: hito.texto })}
                        >
                          <FiPlus /> Agregar a tarea
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <div className={style.hitoAddRow}>
                <input
                  className={style.input}
                  value={hitoNuevo}
                  onChange={(e) => setHitoNuevo(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      agregarHito(meta);
                    }
                  }}
                  placeholder="Nuevo hito…"
                />
                <button type="button" className={style.btnChico} onClick={() => agregarHito(meta)}>
                  <FiPlus /> Agregar
                </button>
              </div>
              {todosHechos && meta.estado === "activa" ? (
                <p className={style.festejo}>🎉 ¡Todos los hitos cumplidos! Marcala como completada.</p>
              ) : null}
            </div>
          ) : null}

          {meta.medicion === "numero" ? (
            <div className={style.hitosBox}>
              <p className={style.boxLabel}>Avance</p>
              <p className={style.numeroActual}>
                {esSimbolo(meta.unidad) ? conUnidad(meta.actualNumero, meta.unidad) : fmtNum(meta.actualNumero)}{" "}
                <span className={style.numeroObjetivo}>de {conUnidad(meta.objetivoNumero, meta.unidad)}</span>
              </p>
              <div className={style.hitoAddRow}>
                <input
                  className={style.input}
                  type="number"
                  value={numeroNuevo}
                  onChange={(e) => setNumeroNuevo(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      registrarAvance(meta);
                    }
                  }}
                  placeholder={`Nuevo valor (${meta.unidad || "total"} acumulado)`}
                />
                <button type="button" className={`${style.btnChico} ${style.btnGuardarAvance}`} onClick={() => registrarAvance(meta)}>
                  <FiCheck /> Guardar
                </button>
              </div>
            </div>
          ) : null}

          {meta.medicion === "manual" ? (
            <div className={style.hitosBox}>
              <div className={style.manualHead}>
                <p className={style.boxLabel}>Avance manual</p>
                {meta.progresoManualAt ? (
                  <span className={style.manualFecha}>
                    Última actualización: {fmtFechaCorta(meta.progresoManualAt)}
                  </span>
                ) : (
                  <span className={style.manualFecha}>Todavía sin actualizar</span>
                )}
              </div>
              <div className={style.manualRow}>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={meta.progresoManual || 0}
                  className={style.slider}
                  onChange={(e) => cambiarManual(meta, Number(e.target.value))}
                  onMouseUp={(e) => guardarManual(meta, Number(e.target.value))}
                  onTouchEnd={(e) => guardarManual(meta, Number(e.target.value))}
                  onKeyUp={(e) => guardarManual(meta, Number(e.target.value))}
                  aria-label="Avance manual"
                />
                <label className={style.manualPct}>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={meta.progresoManual || 0}
                    onChange={(e) => cambiarManual(meta, Number(e.target.value))}
                    onBlur={(e) => guardarManual(meta, Number(e.target.value))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        guardarManual(meta, Number(e.target.value));
                      }
                    }}
                    aria-label="Porcentaje de avance"
                  />
                  <span>%</span>
                </label>
              </div>
              <div className={style.manualQuick}>
                <button type="button" className={style.manualBtn} onClick={() => sumarManual(meta, -10)} disabled={(meta.progresoManual || 0) <= 0}>
                  −10
                </button>
                <button type="button" className={style.manualBtn} onClick={() => sumarManual(meta, 10)} disabled={(meta.progresoManual || 0) >= 100}>
                  +10
                </button>
                <button type="button" className={style.manualBtn} onClick={() => sumarManual(meta, 25)} disabled={(meta.progresoManual || 0) >= 100}>
                  +25
                </button>
                <button type="button" className={`${style.manualBtn} ${style.manualBtnFull}`} onClick={() => guardarManual(meta, 100)} disabled={(meta.progresoManual || 0) >= 100}>
                  Al 100%
                </button>
              </div>
            </div>
          ) : null}

          {/* Meta vencida: extender o cerrar, igual que en la card */}
          {meta.estado === "activa" && daysUntil(meta.fechaObjetivo) !== null && daysUntil(meta.fechaObjetivo) < 0 ? (
            <div className={style.panelVencida}>
              <p className={style.panelVencidaTexto}>
                <FiClock /> {dueLabel(meta)}
              </p>
              {renderVencidaAcciones(meta)}
            </div>
          ) : null}

          {/* Acciones de estado */}
          <div className={style.panelAcciones}>
            {meta.estado === "activa" ? (
              <>
                <button
                  type="button"
                  className={style.btnCompletar}
                  onClick={() => cambiarEstado(meta, "completada")}
                >
                  <FiCheckCircle /> Marcar completada
                </button>
                <button
                  type="button"
                  className={style.btnSecundario}
                  onClick={() => cambiarEstado(meta, "pausada")}
                >
                  <FiPause /> Pausar
                </button>
              </>
            ) : (
              <button
                type="button"
                className={style.btnCompletar}
                onClick={() => cambiarEstado(meta, "activa")}
              >
                <FiPlay /> {meta.estado === "completada" ? "Reabrir meta" : "Reactivar"}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  /* ===== Formulario crear / editar ===== */
  const renderForm = () => (
    <div className={style.overlay} onClick={() => setForm(null)}>
      <form className={style.panel} onClick={(e) => e.stopPropagation()} onSubmit={guardarForm}>
        <div className={style.panelHead}>
          <h2 className={style.panelTitulo}>{form.id ? "Editar meta" : "Nueva meta"}</h2>
          <button
            type="button"
            className={style.iconBtn}
            onClick={() => setForm(null)}
            aria-label="Cerrar"
          >
            <FiX />
          </button>
        </div>

        <label className={style.campo}>
          <span>¿Qué querés lograr?</span>
          <input
            className={style.input}
            value={form.titulo}
            onChange={(e) => setForm({ ...form, titulo: e.target.value })}
            placeholder="Ej: Ahorrar para el viaje a Bariloche"
            autoFocus
          />
        </label>

        <label className={style.campo}>
          <span>Detalle (opcional)</span>
          <textarea
            className={`${style.input} ${style.textarea}`}
            value={form.descripcion}
            onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
            placeholder="Por qué importa, cómo lo vas a encarar…"
            rows={2}
          />
        </label>

        <div className={style.campo}>
          <span>Plazo</span>
          <div className={style.chipsRow}>
            {HORIZONTES.map((h) => (
              <button
                key={h.value}
                type="button"
                className={`${style.chip} ${form.horizonte === h.value ? style.chipActivo : ""}`}
                onClick={() => setForm({ ...form, horizonte: h.value })}
                title={h.hint}
              >
                {h.label}
              </button>
            ))}
          </div>
        </div>

        <div className={style.dosCol}>
          <label className={style.campo}>
            <span>Área</span>
            {areaNueva === null ? (
              <select
                className={style.input}
                value={form.area}
                onChange={(e) => {
                  if (e.target.value === NUEVA_AREA) {
                    setAreaNueva("");
                    return;
                  }
                  setForm({ ...form, area: e.target.value });
                }}
              >
                <option value="">Sin área</option>
                {(form.area &&
                !areasSugeridas(metas, areasExtra).some(
                  (a) => a.toLowerCase() === form.area.toLowerCase()
                )
                  ? [form.area, ...areasSugeridas(metas, areasExtra)]
                  : areasSugeridas(metas, areasExtra)
                ).map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
                <option value={NUEVA_AREA}>+ Agregar nueva…</option>
              </select>
            ) : (
              <div className={style.areaNuevaRow}>
                <input
                  className={style.input}
                  autoFocus
                  value={areaNueva}
                  onChange={(e) => setAreaNueva(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      confirmarAreaNueva();
                    }
                    if (e.key === "Escape") {
                      e.preventDefault();
                      setAreaNueva(null);
                    }
                  }}
                  placeholder="Nombre del área (ej: Estudio)"
                  maxLength={60}
                />
                <button type="button" className={style.areaNuevaOk} onClick={confirmarAreaNueva}>
                  <FiCheck /> Agregar
                </button>
                {/* Cancelar como texto, debajo del campo */}
                <button
                  type="button"
                  className={style.areaNuevaCancel}
                  onClick={() => setAreaNueva(null)}
                >
                  Cancelar
                </button>
              </div>
            )}
          </label>

          <label className={style.campo}>
            <span>Fecha objetivo (opcional)</span>
            <input
              className={style.input}
              type="date"
              value={form.fechaObjetivo}
              onChange={(e) => setForm({ ...form, fechaObjetivo: e.target.value })}
            />
          </label>
        </div>

        <div className={style.campo}>
          <span>¿Cómo medís el avance?</span>
          <div className={style.chipsRow}>
            {MEDICIONES.map((m) => (
              <button
                key={m.value}
                type="button"
                className={`${style.chip} ${form.medicion === m.value ? style.chipActivo : ""}`}
                onClick={() => setForm({ ...form, medicion: m.value })}
                title={m.hint}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {form.medicion === "numero" ? (
          <div className={style.tresCol}>
            <label className={style.campo}>
              <span>Objetivo</span>
              <input
                className={style.input}
                type="number"
                value={form.objetivoNumero}
                onChange={(e) => setForm({ ...form, objetivoNumero: e.target.value })}
                placeholder="500000"
              />
            </label>
            <label className={style.campo}>
              <span>Llevás</span>
              <input
                className={style.input}
                type="number"
                value={form.actualNumero}
                onChange={(e) => setForm({ ...form, actualNumero: e.target.value })}
                placeholder="0"
              />
            </label>
            <label className={style.campo}>
              <span>Unidad (qué contás)</span>
              <input
                className={style.input}
                value={form.unidad}
                onChange={(e) => setForm({ ...form, unidad: e.target.value })}
                placeholder="ej: libros, km, $, clientes"
                maxLength={20}
              />
              <small className={style.campoAyuda}>
                Es solo la etiqueta: con objetivo 12 y unidad “libros” la meta muestra “3 de 12 libros”.
              </small>
            </label>
          </div>
        ) : null}

        {form.medicion === "hitos" ? (
          <div className={style.campo}>
            <span>Hitos iniciales (podés sumar más después)</span>
            {form.hitos.map((hito, indice) => (
              <div key={indice} className={style.hitoAddRow}>
                <input
                  className={style.input}
                  value={hito.texto}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      hitos: form.hitos.map((h, i) =>
                        i === indice ? { ...h, texto: e.target.value } : h
                      ),
                    })
                  }
                />
                <button
                  type="button"
                  className={style.iconBtn}
                  onClick={() =>
                    setForm({ ...form, hitos: form.hitos.filter((_, i) => i !== indice) })
                  }
                  aria-label="Quitar hito"
                >
                  <FiTrash2 />
                </button>
              </div>
            ))}
            <button
              type="button"
              className={style.btnChico}
              onClick={() =>
                setForm({ ...form, hitos: [...form.hitos, { texto: "", hecho: false }] })
              }
            >
              <FiPlus /> Agregar hito
            </button>
          </div>
        ) : null}

        <button type="submit" className={style.btnGuardar} disabled={!form.titulo.trim() || guardando}>
          {guardando ? "Guardando…" : form.id ? "Guardar cambios" : "Crear meta"}
        </button>
      </form>
    </div>
  );

  return (
    <section className={style.page}>
      <header className={style.header}>
        <div>
          <h1 className={style.titulo}>Tus metas</h1>
        </div>
        <div className={style.headerAcciones}>
          <button
            type="button"
            className={`${style.btnGrafico} ${chartsOpen ? style.btnGraficoActivo : ""}`}
            onClick={toggleCharts}
            aria-label={chartsOpen ? "Ocultar gráficos" : "Ver gráficos"}
            title={chartsOpen ? "Ocultar gráficos" : "Ver gráficos"}
            aria-pressed={chartsOpen}
          >
            <FiBarChart2 />
          </button>
          <button type="button" className={style.btnNueva} onClick={abrirCrear}>
            <FiPlus /> Nueva meta
          </button>
        </div>
      </header>

      {/* Resumen */}
      <div className={style.stats}>
        <div className={style.stat}>
          <FiTarget className={style.statIcono} />
          <div>
            <p className={style.statNumero}>{stats.activas}</p>
            <p className={style.statLabel}>En curso</p>
          </div>
        </div>
        <div className={style.stat}>
          <FiCheckCircle className={style.statIcono} />
          <div>
            <p className={style.statNumero}>{stats.completadas}</p>
            <p className={style.statLabel}>Completadas</p>
          </div>
        </div>
        <div className={style.stat}>
          <FiFlag className={style.statIcono} />
          <div>
            <p className={style.statNumero}>{stats.promedio}%</p>
            <p className={style.statLabel}>Avance promedio</p>
          </div>
        </div>
      </div>

      {/* Gráficos (se abren con el ícono del header) */}
      {chartsOpen && metas.length > 0 ? (
        <div className={style.chartsGrid}>
          <article className={style.chartCard}>
            <p className={style.boxLabel}>Metas por plazo</p>
            <div className={style.donutLayout}>
              <div
                className={style.donut}
                style={{ background: buildConicGradient(plazoItems) }}
                aria-label="Distribución de metas por plazo"
              >
                <div className={style.donutHole}>
                  <strong>{plazoItems.reduce((a, i) => a + i.value, 0)}</strong>
                  <span>en curso</span>
                </div>
              </div>
              <div className={style.legend}>
                {plazoItems.map((item) => (
                  <div key={item.label} className={style.legendItem}>
                    <i style={{ background: item.color }} />
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </div>
                ))}
              </div>
            </div>
          </article>

          <article className={style.chartCard}>
            <p className={style.boxLabel}>Avance de tus metas activas</p>
            {barrasAvance.length === 0 ? (
              <p className={style.vacio}>No hay metas activas para graficar.</p>
            ) : (
              <div className={style.candleScroll}>
                {(() => {
                  const LEFT = 32; // espacio para las etiquetas del eje Y (%)
                  const COL = 92; // ancho por meta (más aire para el texto)
                  const BODY = 10; // ancho del cuerpo de la vela (fina)
                  const TOP = 12;
                  const PLOT = 150;
                  const H = 210;
                  const W = LEFT + barrasAvance.length * COL;
                  const yVal = (pct) => TOP + PLOT * (1 - pct / 100);
                  return (
                    <svg
                      className={style.candleSvg}
                      width={W}
                      height={H}
                      viewBox={`0 0 ${W} ${H}`}
                      role="img"
                    >
                      {/* Grilla + eje Y en % */}
                      {[0, 25, 50, 75, 100].map((pct) => (
                        <g key={pct}>
                          <line
                            x1={LEFT}
                            x2={W}
                            y1={yVal(pct)}
                            y2={yVal(pct)}
                            stroke="var(--border-color)"
                            strokeWidth="1"
                            opacity="0.4"
                          />
                          <text
                            x={LEFT - 6}
                            y={yVal(pct) + 3}
                            textAnchor="end"
                            className={style.candleAxis}
                          >
                            {pct}%
                          </text>
                        </g>
                      ))}
                      {/* Velas: mecha = rango hasta 100%, cuerpo = avance */}
                      {barrasAvance.map((b, i) => {
                        const color = PLAZO_COLORS[b.horizonte] || "#5dc72d";
                        const cx = LEFT + i * COL + COL / 2;
                        const bodyTop = yVal(b.progreso);
                        const bodyH = Math.max(3, yVal(0) - bodyTop);
                        // Partimos el título en hasta 2 líneas para que entre debajo de la vela.
                        const palabras = b.titulo.split(" ");
                        let l1 = "";
                        let l2 = "";
                        palabras.forEach((w) => {
                          if (!l2 && `${l1} ${w}`.trim().length <= 13) l1 = `${l1} ${w}`.trim();
                          else l2 = `${l2} ${w}`.trim();
                        });
                        if (l2.length > 13) l2 = `${l2.slice(0, 12)}…`;
                        return (
                          <g key={b.id}>
                            <title>{`${b.titulo} · ${b.progreso}%`}</title>
                            {/* Cuerpo: avance */}
                            <rect
                              x={cx - BODY / 2}
                              y={bodyTop}
                              width={BODY}
                              height={bodyH}
                              rx="3"
                              fill={color}
                            />
                            {/* % arriba de la barra */}
                            <text
                              x={cx}
                              y={Math.max(bodyTop - 6, 10)}
                              textAnchor="middle"
                              className={style.candleValue}
                              fill={color}
                            >
                              {b.progreso}%
                            </text>
                            <text
                              x={cx}
                              y={H - 18}
                              textAnchor="middle"
                              className={style.candleLabelText}
                            >
                              {l1}
                            </text>
                            {l2 ? (
                              <text
                                x={cx}
                                y={H - 6}
                                textAnchor="middle"
                                className={style.candleLabelText}
                              >
                                {l2}
                              </text>
                            ) : null}
                          </g>
                        );
                      })}
                    </svg>
                  );
                })()}
              </div>
            )}
          </article>
        </div>
      ) : null}

      {/* Filtros */}
      <div className={style.filtros}>
        <div className={style.chipsRow}>
          {[{ value: "todas", label: "Todas" }, ...HORIZONTES].map((h) => (
            <button
              key={h.value}
              type="button"
              className={`${style.chip} ${horizonteFiltro === h.value ? style.chipActivo : ""}`}
              onClick={() => setHorizonteFiltro(h.value)}
            >
              {h.label}
            </button>
          ))}
        </div>
        <div className={style.chipsRow}>
          {[
            { value: "activa", label: "Activas" },
            { value: "pausada", label: "Pausadas" },
            { value: "completada", label: "Completadas" },
            { value: "no_cumplida", label: "No cumplidas" },
            { value: "todas", label: "Todas" },
          ].map((e) => (
            <button
              key={e.value}
              type="button"
              className={`${style.chip} ${estadoFiltro === e.value ? style.chipActivo : ""}`}
              onClick={() => setEstadoFiltro(e.value)}
            >
              {e.label}
            </button>
          ))}
        </div>
      </div>

      {/* Lista */}
      {cargando ? (
        <p className={style.vacio}>Cargando tus metas…</p>
      ) : visibles.length === 0 ? (
        <div className={style.vacioBox}>
          <FiTarget className={style.vacioIcono} />
          <p className={style.vacioTitulo}>
            {metas.length === 0 ? "Todavía no tenés metas" : "Nada por acá con estos filtros"}
          </p>
          <p className={style.vacioTexto}>
            {metas.length === 0
              ? "Arrancá con una meta corta y concreta: es la forma más fácil de agarrar ritmo."
              : "Probá cambiando el plazo o el estado."}
          </p>
          {metas.length === 0 ? (
            <button type="button" className={style.btnNueva} onClick={abrirCrear}>
              <FiPlus /> Crear mi primera meta
            </button>
          ) : null}
        </div>
      ) : (
        <div className={style.grid}>{visibles.map(renderCard)}</div>
      )}

      {detalle ? renderDetalle() : null}
      {form ? renderForm() : null}

      {/* Crear tarea desde un hito (queda vinculada) */}
      {taskForm ? (
        <div className={style.overlay} onClick={() => setTaskLink(null)}>
          <form
            className={style.panel}
            onClick={(e) => e.stopPropagation()}
            onSubmit={guardarTareaDeHito}
          >
            <div className={style.panelHead}>
              <h2 className={style.panelTitulo}>Agregar hito a Tareas</h2>
              <button
                type="button"
                className={style.iconBtn}
                onClick={() => setTaskLink(null)}
                aria-label="Cerrar"
              >
                <FiX />
              </button>
            </div>

            <label className={style.campo}>
              <span>Tarea</span>
              <input
                className={style.input}
                value={taskForm.meta}
                onChange={(e) => setTaskForm({ ...taskForm, meta: e.target.value })}
                autoFocus
              />
            </label>

            <div className={style.dosCol}>
              <label className={style.campo}>
                <span>Fecha</span>
                <input
                  className={style.input}
                  type="date"
                  value={taskForm.fecha}
                  onChange={(e) => setTaskForm({ ...taskForm, fecha: e.target.value })}
                />
              </label>
              <label className={style.campo}>
                <span>Horario (opcional)</span>
                <input
                  className={style.input}
                  type="time"
                  value={taskForm.horario}
                  onChange={(e) => setTaskForm({ ...taskForm, horario: e.target.value })}
                />
              </label>
            </div>

            <div className={style.campo}>
              <span>Prioridad</span>
              <div className={style.chipsRow}>
                {URGENCIAS.map((u) => (
                  <button
                    key={u.value}
                    type="button"
                    className={`${style.chip} ${taskForm.urgencia === u.value ? style.chipActivo : ""}`}
                    onClick={() => setTaskForm({ ...taskForm, urgencia: u.value })}
                  >
                    {u.label}
                  </button>
                ))}
              </div>
            </div>

            <div className={style.campo}>
              <span>Color de fondo</span>
              <div className={style.colorRow}>
                {COLORES_TAREA.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    className={`${style.colorSwatch} ${taskForm.color === c.key ? style.colorSwatchActive : ""}`}
                    style={{ background: c.grad }}
                    onClick={() => setTaskForm({ ...taskForm, color: c.key })}
                    aria-label={`Color ${c.key}`}
                  >
                    {taskForm.color === c.key ? <FiCheck /> : null}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="submit"
              className={style.btnGuardar}
              disabled={!taskForm.meta.trim() || taskSaving}
            >
              {taskSaving ? "Guardando…" : "Crear tarea"}
            </button>
          </form>
        </div>
      ) : null}
    </section>
  );
}

export default MetasPage;
